import { useMemo } from 'react';
import { InlineLoading } from '@carbon/react';
import type { SortingState } from '@tanstack/react-table';

import {
  DataTable,
  formatCellValue,
  type DataTableColumnDef,
  type TableState,
} from 'components/data-table';
import { FormattedCount } from 'components/formatted-count';

import type { BBox } from 'components/query-map';

import type { MapQueryResultRow } from './types';
import { resultRowId } from './utils';
import styles from './map-query-source.module.scss';

const DECIMAL = { type: 'decimal' as const };
// Typed columns render a missing value as a dash.
const TEXT = { type: 'text' as const };

// Column ids are the search's sort attributes (the server underscores them).
const COLUMNS: DataTableColumnDef<MapQueryResultRow>[] = [
  { id: 'assetName', accessorKey: 'name', header: 'Asset Name', meta: TEXT },
  // The Tracts come first and the wellbore and LTD assets after, whatever the
  // sort, so the type is not a sort.
  {
    id: 'assetType',
    accessorKey: 'assetType',
    header: 'Asset Type',
    meta: TEXT,
    enableSorting: false,
  },
  {
    id: 'entityName',
    header: 'Entity',
    accessorFn: (row) =>
      row.entities
        .map((entity) => entity.name)
        .filter(Boolean)
        .join(', ') || null,
    meta: TEXT,
  },
  {
    id: 'surveyArea',
    accessorKey: 'surveyArea',
    header: 'Location',
    meta: TEXT,
  },
  {
    id: 'instrumentType',
    accessorKey: 'instrumentType',
    header: 'Interest Type',
    meta: TEXT,
  },
  {
    id: 'netAcres',
    accessorKey: 'netAcres',
    header: 'Net Acres',
    meta: DECIMAL,
    cell: ({ row }) => {
      const { netAcres, netAcreageUnits } = row.original;
      if (netAcres === null) return '—';
      return [formatCellValue(netAcres, DECIMAL), netAcreageUnits]
        .filter(Boolean)
        .join(' ');
    },
  },
  {
    id: 'grossAcres',
    accessorKey: 'grossAcres',
    header: 'Gross Acres',
    meta: DECIMAL,
  },
];

export interface ResultsTableProps {
  rows: MapQueryResultRow[];
  /** Null before the first search answers. */
  totalCount: number | null;
  isLoading: boolean;
  error: string | null;
  pagination: { page: number; pageSize: number };
  onPaginationChange: (pagination: { page: number; pageSize: number }) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  /** The focused row's id (`resultRowId`). */
  focusedRowId: string | null;
  /**
   * A click on a row, with its id and its asset's box. Rows of assets with no
   * shape cannot be focused.
   */
  onRowClick: (rowId: string, bbox: BBox) => void;
}

/**
 * "Assets (N)": the Map Query results, a page at a time from the server, sorted
 * on the server: the matching and listed Tracts, then the matching wellbore and
 * LTD assets. Clicking a row focuses its asset on the map (again clears it); the
 * focused row shows selected.
 *
 * @param {ResultsTableProps} props - The page, paging, sorting and focus
 * @return {JSX.Element} The results table
 */
export function ResultsTable({
  rows,
  totalCount,
  isLoading,
  error,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  focusedRowId,
  onRowClick,
}: ResultsTableProps) {
  const selection = useMemo(
    () => ({
      selectAll: false,
      toggledNodes: focusedRowId ? [focusedRowId] : [],
    }),
    [focusedRowId]
  );
  const rowsById = useMemo(
    () => new Map(rows.map((row) => [resultRowId(row), row])),
    [rows]
  );

  // In the table's own toolbar row, beside its reset button, so the title sits
  // on the table as the mock's does instead of over an empty toolbar row.
  const header = (
    <header key="header" className={styles.resultsHeader}>
      <div className={styles.resultsTitleRow}>
        <h4 className={styles.resultsTitle}>
          {totalCount === null || isLoading ? (
            'Assets'
          ) : (
            <>
              Assets (<FormattedCount value={totalCount} />)
            </>
          )}
        </h4>
        {isLoading && (
          <InlineLoading
            className={styles.resultsLoading}
            description="Loading assets…"
          />
        )}
      </div>
      <p className={styles.resultsDescription}>
        Assets matching the current filters, and any tracts added by list. All
        of them carry into the package; you can deselect on the next step.
      </p>
    </header>
  );

  return (
    <div className={styles.results}>
      <div className={styles.resultsTable}>
        <DataTable<MapQueryResultRow>
          data={rows}
          columns={COLUMNS}
          getRowId={resultRowId}
          isLoading={isLoading}
          // A failed search says why in place of the rows, not as "no assets match".
          error={error}
          emptyMessage="No assets match the current filters."
          scroll={{
            mode: 'paginated',
            serverDriven: true,
            totalCount: totalCount ?? 0,
          }}
          controlledState={{ pagination, sorting, selection }}
          onPaginationChange={({ page, pageSize }: TableState) =>
            onPaginationChange({ page, pageSize })
          }
          onSortChange={(state: TableState) => onSortingChange(state.sorting)}
          onSelectionChange={({ selection: { toggledNodes } }: TableState) => {
            const row = rowsById.get(toggledNodes[0] ?? '');
            if (row?.bbox) onRowClick(resultRowId(row), row.bbox);
          }}
          leftToolbarActions={[header]}
          fillLeftToolbarActions
          config={{
            selectionType: 'single',
            multiSort: false,
            globalSearch: false,
            columnFilters: false,
            columnVisibility: false,
            columnPinning: false,
            columnReordering: false,
          }}
        />
      </div>
    </div>
  );
}
